export type VoiceRecorderOptions = {
    onAudioData: (audioData: string) => void;
    onStop: () => void;
    onStart: () => void;
    onFirstFrame: () => void;
};

const audioWorkletModuleUrl = new URL('../worklets/audio-worklet.js', import.meta.url);

// Exported for a page that needs its own mic, not the shared singleton below
// (the schema playground's voice call — the Sidekick, mounted on every shell
// page, re-points the singleton's callbacks and stops it whenever its deps move).
export class VoiceRecorder {
    public mediaStream: MediaStream | null = null;
    public audioContext: AudioContext | null = null;
    public workletNode: AudioWorkletNode | null = null;
    public gainNode: GainNode | null = null;
    public isActive: boolean = false;
    public error: string = '';
    public onAudioData: (audioData: string) => void;
    public onStop: () => void;
    public onStart: () => void;
    public onFirstFrame: () => void;
    public hasEmittedFirstFrame: boolean = false;
    // Bumped by every real start and by every stop: a start whose awaits
    // (the mic permission prompt, the worklet load) outlive a stopRecording
    // sees a newer value and backs out instead of switching the mic back on.
    private startGen = 0;

    constructor(options: VoiceRecorderOptions) {
        this.onAudioData = options.onAudioData;
        this.onStop = options.onStop;
        this.onStart = options.onStart;
        this.onFirstFrame = options.onFirstFrame;
    }

    public setMuted(muted: boolean) {
        if (this.gainNode) {
            this.gainNode.gain.setTargetAtTime(muted ? 0 : 1, this.audioContext?.currentTime ?? 0, 0.015);
        }
    }

    public async startRecording() {
        let gen = this.startGen;
        try {
            console.log('startRecording called');
            if (this.isActive) {
                if (this.hasEmittedFirstFrame) this.onFirstFrame();
                return;
            }
            console.log('starting recording');
            gen = ++this.startGen;
            this.isActive = true;
            this.hasEmittedFirstFrame = false;

            if (!this.mediaStream) {
                const stream = await navigator.mediaDevices.getUserMedia({
                    audio: {
                        sampleRate: 16000,
                        channelCount: 1,
                        echoCancellation: true,
                        noiseSuppression: true,
                        // AGC normalizes quiet mics; matches the WebRTC path (browser
                        // default). Was disabled (ababd917f) chasing false barge-ins —
                        // echoCancellation is the actual defense there.
                        autoGainControl: true,
                    }
                });
                // stopRecording ran while the permission prompt was open: release
                // the mic we just got rather than keep it (and reuse it next start).
                if (gen !== this.startGen) {
                    stream.getTracks().forEach((track) => track.stop());
                    return;
                }
                this.mediaStream = stream;
            }

            if (!this.audioContext) {
                this.audioContext = new AudioContext({ sampleRate: 16000 });
            }

            const source = this.audioContext.createMediaStreamSource(this.mediaStream);
            this.gainNode = this.audioContext.createGain();
            await this.audioContext.audioWorklet.addModule(audioWorkletModuleUrl);
            // stopRecording ran during the load: it already closed the context,
            // stopped the tracks and dropped the gain — build nothing on them.
            if (gen !== this.startGen) return;
            this.workletNode = new AudioWorkletNode(this.audioContext, 'simple-processor');
            this.workletNode.port.onmessage = (e: MessageEvent) => {
                try {
                    const isFirstFrame = !this.hasEmittedFirstFrame;
                    this.onAudioData(e.data as string);
                    if (isFirstFrame) {
                        this.hasEmittedFirstFrame = true;
                        this.onFirstFrame();
                    }
                } catch (err) {
                    console.error('Error in onmessage', err);
                    this.stopRecording();
                }
            };
            this.workletNode.connect(this.audioContext.destination);
            source.connect(this.gainNode);
            this.gainNode.connect(this.workletNode);
            this.error = '';
            if (this.onStart) this.onStart();
        } catch (err) {
            // A start that a stop (and maybe a newer start) superseded must not
            // report its failure or clear the newer start's isActive.
            if (gen !== this.startGen) return;
            // @ts-ignore
            this.error = 'Could not access microphone. Please grant permission. ' + (err?.message ?? '');
            if (this.onStop) this.onStop();
            this.isActive = false;
        }
    }

    public stopRecording() {
        this.startGen++;
        this.isActive = false;
        this.hasEmittedFirstFrame = false;
        if (this.workletNode) {
            this.workletNode.port.onmessage = null;
            this.workletNode.disconnect();
            this.workletNode = null;
            console.log('workletNode disconnected');
        }
        if (this.gainNode) {
            this.gainNode.disconnect();
            this.gainNode = null;
        }
        if (this.audioContext) {
            this.audioContext.close();
            this.audioContext = null;
            console.log('audioContext closed');
        }
        if (this.mediaStream) {
            const tracks = this.mediaStream.getTracks();
            this.mediaStream = null;
            tracks?.forEach((track: MediaStreamTrack) => track.stop());
            console.log('tracks stopped');
        }
        this.error = '';
        console.log('stopRecording done');
        if (this.onStop) this.onStop();
    }

    public getError() {
        return this.error;
    }

    public isCurrentlyRecording() {
        return this.isActive;
    }
}

//fix typing 
declare global {
    interface Window {
        vrs: VoiceRecorder | null;
        getVoiceRecorderSingleton: (onAudioData: (audioData: string) => void, onStop: () => void, onStart: () => void, onFirstFrame?: () => void) => VoiceRecorder;
    }
}
window.vrs = null;

function getVoiceRecorderSingleton(onAudioData: (audioData: string) => void, onStop: () => void, onStart: () => void, onFirstFrame: () => void = () => {}) {
    if (!window.vrs) {
        window.vrs = new VoiceRecorder({
            onAudioData: onAudioData,
            onStop: onStop,
            onStart: onStart,
            onFirstFrame: onFirstFrame,
        });
    }
    else {
        // Update callbacks WITHOUT stopping - recording can continue with new callbacks
        // The useEffect will handle start/stop based on isRecording state
        window.vrs.onAudioData = onAudioData;
        window.vrs.onStop = onStop;
        window.vrs.onStart = onStart;
        window.vrs.onFirstFrame = onFirstFrame;
    }
    return window.vrs;
}

window.getVoiceRecorderSingleton = getVoiceRecorderSingleton;
export default getVoiceRecorderSingleton;